import posthog from 'posthog-js';

import {
  POSTHOG_PROXY_PATH,
  posthogUiHost
} from '@/lib/posthog/posthog-proxy.mjs';

import {
  areAllFramesFromCcm19,
  areAllFramesFromTikTokPixel,
  areAllFramesMasked,
  isIgnoredErrorMessage,
  isInAppInjectedError,
  isRecaptchaTeardownError
} from '@/lib/ignored-errors';
import { scrubAnalyticsEventUrls } from '@/lib/scrub-pii';
import {
  ensureSessionSource,
  readCampaign,
  registerCampaign,
  StoredCampaign,
  UTM_STORAGE_KEY
} from './utm-attribution';

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST;

let isInitialized = false;

let pendingUtm: StoredCampaign | null = null;

export const stashPendingUtm = (entry: StoredCampaign) => {
  pendingUtm = entry;
};

type InitListener = () => void;
const initListeners = new Set<InitListener>();

/**
 * Run `listener` once PostHog is initialized (immediately if it already is).
 * Lets pre-consent work — impression timers, queued events — resume instead of
 * being dropped. Returns an unsubscribe for cleanup.
 */
export const onPostHogInitialized = (listener: InitListener): (() => void) => {
  if (isInitialized) {
    listener();
    return () => {};
  }

  // Without a key `initPostHog` returns before flipping the flag, so a listener
  // parked here could never run.
  if (typeof window === 'undefined' || !POSTHOG_KEY) return () => {};

  initListeners.add(listener);

  return () => {
    initListeners.delete(listener);
  };
};

/**
 * Initialize PostHog analytics after consent is granted
 * CRITICAL: Only call after user has accepted analytics cookies
 */
export const initPostHog = () => {
  if (typeof window === 'undefined' || isInitialized) return;

  if (!POSTHOG_KEY) {
    if (process.env.NODE_ENV === 'development') {
      throw new Error(
        'NEXT_PUBLIC_POSTHOG_KEY variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once NEXT_PUBLIC_POSTHOG_KEY is configured'
      );
    }
    return;
  }

  if (!POSTHOG_HOST) {
    if (process.env.NODE_ENV === 'development') {
      throw new Error(
        'NEXT_PUBLIC_POSTHOG_HOST variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once NEXT_PUBLIC_POSTHOG_HOST is configured'
      );
    }
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_PROXY_PATH,
    ui_host: posthogUiHost(POSTHOG_HOST),
    // Pins the SDK's *default* config to a dated baseline. Without it the SDK
    // reports `$config_defaults: 'unset'` and a posthog-js upgrade can shift
    // capture behaviour underneath us — which, on a consent-gated funnel, is
    // exactly the thing that must not move on its own. Two effects we want at
    // this date: `internal_or_test_user_hostname` flags localhost/127.0.0.1
    // visitors with `$internal_or_test_user: true`, so local runs against the
    // production project are filterable instead of indistinguishable from real
    // traffic; and rageclick gains a content ignorelist and stops counting text
    // selection as rage, which matters now that autocapture runs on the landing
    // page. Raising this date is a deliberate review, not a routine bump.
    defaults: '2026-05-30',
    // Everything below is reachable only from `initPostHog`, which CCM19 calls
    // once the visitor grants analytics consent — the consent gate is upstream,
    // so these are opt-in by construction. That is the floor, not the ceiling:
    // consent covers *analytics*, so what is enabled here stays within what the
    // Datenschutzerklärung actually describes. See the session-replay note below.
    //
    // `$pageview`/`$pageleave` are PostHog's own events, distinct from the
    // hand-rolled `page_view` above, so no existing dashboard double-counts.
    // `history_change` rather than `true`: this is an App Router SPA, and the
    // plain page-load default would miss every client-side navigation.
    capture_pageview: 'history_change',
    capture_pageleave: true,
    // Heatmaps are `$heatmap` events — viewport coordinates, no element text,
    // no inputs — so they carry no personal data and stay on site-wide.
    enable_heatmaps: true,
    // Autocapture DOES read element text and attributes, so it is confined to
    // the one public marketing page the experiment runs on. That is where the
    // click/rageclick data is worth having, and it keeps autocapture off
    // registration, login, OTP, identity-verification and profile entirely —
    // the pages where a stray label could carry personal data.
    autocapture: {
      url_allowlist: ['.*/what-is-ihrHelden.*'],
      css_selector_ignorelist: [
        '.ph-no-autocapture',
        '[data-ph-no-autocapture]'
      ]
    },
    // Session replay stays OFF. Data residency is not the reason — production
    // is PostHog EU. The reason is what a recording of *this* funnel contains:
    // work-email, employer, profession and document-upload screens, for an
    // audience of care and emergency workers. That needs a DPIA and an explicit
    // replay purpose in the CCM19 banner and the Datenschutzerklärung before it
    // is switched on — a legal step, not a config flip. Masking alone does not
    // carry it either: `maskAllInputs` leaves on-screen text captured unless
    // `maskTextSelector: '*'`, at which point replay shows little of value.
    disable_session_recording: true,
    // Capture application errors that escape React or promise handlers. Console
    // messages are not error-tracking scope, so they remain excluded.
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false
    },
    // Drop the same third-party noise Faro already discards. Without this the
    // reporters disagree: the ignore list used to live inside the client config, so
    // enabling `capture_exceptions` sent every entry on it straight to PostHog.
    // Returning null discards the event; returning it unchanged lets it through.
    before_send: (event: any) => {
      if (!event) return event;
      scrubAnalyticsEventUrls(event);
      if (event.event !== '$exception') return event;

      const props = event.properties ?? {};

      // A throw whose every stack frame is a Safari-masked extension frame is
      // not our code — drop it by frame origin rather than message, since its
      // message is a mangled name that changes on each extension rebuild.
      // Frames live only on `$exception_list`, so gather them from there.
      const frames = (
        Array.isArray(props.$exception_list) ? props.$exception_list : []
      ).flatMap((exception: any) => exception?.stacktrace?.frames ?? []);
      if (
        areAllFramesMasked(frames) ||
        areAllFramesFromCcm19(frames) ||
        areAllFramesFromTikTokPixel(frames) ||
        isInAppInjectedError({
          userAgent: navigator.userAgent,
          pageUrl: window.location.href,
          frames
        })
      ) {
        return null;
      }

      // PostHog models one throw as a list of exceptions (an error plus its
      // `cause` chain), so check every message, not just the first.
      const messages: unknown[] = [
        ...(Array.isArray(props.$exception_list)
          ? props.$exception_list.map((e: any) => e?.value)
          : []),
        ...(Array.isArray(props.$exception_values)
          ? props.$exception_values
          : []),
        props.$exception_message
      ];

      return messages.some(
        (message) =>
          isIgnoredErrorMessage(message) || isRecaptchaTeardownError(message)
      )
        ? null
        : event;
    },
    persistence: 'localStorage+cookie',
    // Disable bot filtering in development (allows events from debuggers/automated tests)
    // In production, bot filtering remains enabled to prevent analytics pollution
    opt_out_useragent_filter: process.env.NODE_ENV === 'development',
    loaded: (ph: any) => {
      if (process.env.NODE_ENV === 'development') {
        ph.debug();
      }
    }
  } as any);

  posthog.opt_in_capturing();
  isInitialized = true;

  // Purge legacy super props still stuck in returning visitors' browsers (old fix never unregistered them).
  posthog.unregister('previous_placement_type');
  posthog.unregister('previous_section');

  // Apply any UTM params that were stored before consent was granted
  try {
    if (pendingUtm) {
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(pendingUtm));
      pendingUtm = null;
    }
    const campaign = readCampaign();
    if (campaign) {
      registerCampaign(campaign);
      ensureSessionSource(campaign.utm_source);
    }
  } catch {}

  // Deferred: callers identify the user immediately after init
  // (`startTracking` → `reIdentifyIfLoggedIn`), and that call is what registers
  // the segment super properties. Flushing inline would replay every queued
  // impression against the anonymous distinct_id with no `segment_*`, so those
  // events would vanish from exactly the segment-split CTR they exist for.
  queueMicrotask(() => {
    if (!isInitialized) return;

    const listeners = [...initListeners];
    initListeners.clear();
    listeners.forEach((listener) => {
      try {
        listener();
      } catch {}
    });
  });
};

/**
 * Shutdown PostHog and stop all data collection.
 * Called when user withdraws consent.
 */
export const shutdownPostHog = () => {
  if (!isInitialized) return;

  posthog.opt_out_capturing();
  posthog.reset();
  isInitialized = false;
  pendingUtm = null;
  // Consent was withdrawn, so anything still waiting on init must not be
  // replayed if the user later accepts again.
  initListeners.clear();
};

/**
 * Check if PostHog is initialized
 */
export const isPostHogInitialized = () => isInitialized;
